/* globals showModalGeneric */
import React, { useState } from "react";
import Modal from "common/Modal";
import { safeStr, requestOnlineBqnIds, invalid } from "common/api";

export function toBool(val) {
  var num = Number(val);
  if (isNaN(num)) {
    return val == "yes" ? true : false;
  } else {
    return val;
  }
}

export function fromBool(val) {
  var num = Number(val);
  if (typeof val === "boolean") {
    return val ? "yes" : "no";
  } else if (val == undefined) {
    return "no";
  } else {
    return val;
  }
}

export function toBlockOrNumber(val) {
  var num = Number(val);
  if (isNaN(num)) {
    return "block";
  } else {
    return (Number(val) / 1000).toFixed(3);
  }
}

export function checkPercentage(attr) {
  return attr.endsWith("%");
}

export const parsePolicies = (response) => {
  const [head, ...rows] = response.trim("\n").split("\n");
  const headAttrs = head.trim().split(/\s+/);
  const nameIndex = headAttrs.indexOf('NAME');
  return rows.reduce((acc, row) => {
    const attrs = row.trim().split(/\s+/);
    const policyName = attrs[nameIndex];
    const policyValues = attrs.reduce((accValues, attr, index) => {
      accValues[headAttrs[index].toLowerCase()] = attr;
      return accValues;
    }, {});
    acc[policyName] = policyValues;
    return acc;
  }, {});
};

export const getPolicies = (type) => {
  const command = `show policy ${type}${type === "rate" ? "" : " web"}`;
  return ifCl.run(command).then(parsePolicies);
};

export const BlockModal = ({ onClose, onApply }) => {
  const [open, setOpen] = useState(true);

  if (!open) {
    return null;
  }

  const handleApply = () => {
    onClose();
    onApply();
  };

  return (
    <Modal
      title="Are you sure ?"
      superIcon="warning"
      onClose={onClose}
      closeLabel="CANCEL"
      content={() => <>Traffic will be blocked when firing this policy</>}
      onApply={handleApply}
      applyLabel="OK"
    />
  );
};

export const parsePolicy = (response, responsePolicyMap) => {
  const lines = response.trim("\n").split("\n");
  const { policy } = lines.reduce(
    (acc, line) => {
      const [key, value] = line.split(":");
      if (/^\s/.test(key)) {
        const trimmedKey = key.trim();
        const policyPropMapped =
          responsePolicyMap[`${acc.base}: ${trimmedKey}:`];
        if (policyPropMapped) {
          const policyPropName = policyPropMapped.name;
          const trimmedValue = value.trim();
          const policyPropValue = policyPropMapped.valueF(trimmedValue);
          acc.policy[policyPropName] = policyPropValue;
        }
      } else {
        acc.base = key;
        const policyPropMapped = responsePolicyMap[`${key}:`];
        if (policyPropMapped) {
          const policyPropName = policyPropMapped.name;
          const trimmedValue = value.trim();
          const policyPropValue = policyPropMapped.valueF(trimmedValue);
          acc.policy[policyPropName] = policyPropValue;
        }
      }
      return acc;
    },
    { base: "", policy: {} }
  );
  return policy;
};

export const getPolicy = (policy) => ifCl.run(`show policy ${safeStr(policy)}`);

export function setBrateValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return Math.floor(Number(value) * 1000).toString();
  }
}

export function setBdurValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return value;
  }
}

export function setBthrValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return Math.floor(Number(value) * 1000).toString();
  }
}

export function setBthrwValue(value) {
  if (value === SSHBTHRW_DFLT_VAL) {
    return "default";
  } else {
    return value;
  }
}

export const SSHRATE_DFLT_VAL = 100000;
export const SSHZERO_DFLT_VAL = 0;
export const SSHBTHRW_DFLT_VAL = 300;

const _localeSettings = [
  "en-US",
  {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  },
];

function expressAsDataSpeed(value) {
  return value > 999999
    ? `${Number(value / 1000000).toLocaleString(..._localeSettings)} Gbps`
    : value > 999
    ? `${Number(value / 1000).toLocaleString(..._localeSettings)} Mbps`
    : `${Number(value).toLocaleString(..._localeSettings)} Kbps`;
}

function inDataSpeedUnits(value) {
  return value === "no" || value === false
    ? "no"
    : expressAsDataSpeed(parseFloat(value));
}

function splitFieldAndValue(line) {
  return line.split(/\:\s/);
}

function wrapIntoIndentedSpan(text) {
  return `<span style="margin-left: 2ch;">${keepIndentation(text)}</span>`;
}

const NOT_ALLOWED_DYN_POLICY = ["Type", "DOS", "Dynamic update"];

const TIME_FIELDS_DYN_POLICY = [
  "Burst duration",
  "Burst threshold window",
  "Burst threshold duration",
  "Burst transition duration",
];

const SPEED_FIELDS_DYN_POLICY = [
  "Rate",
  "Burst rate",
  "Burst threshold",
  "Burst threshold",
];

function arrangeUnits([field, value]) {
  return TIME_FIELDS_DYN_POLICY.includes(field.trim(" "))
    ? [field, value + " s"]
    : SPEED_FIELDS_DYN_POLICY.includes(field.trim(" "))
    ? [field, inDataSpeedUnits(value)]
    : [field, value];
}

function onlyAllowed([field, _value]) {
  return NOT_ALLOWED_DYN_POLICY.includes(field) !== true;
}

function joinRecord([field, value]) {
  return `${field}: ${value}`;
}

function keepIndentation(fact) {
  return fact.startsWith("  ")
    ? wrapIntoIndentedSpan(fact.replace(/^\s\s/, ""))
    : fact;
}

export function parseClusterResponse(response) {
  const bqnResponses = response.trim().split("\n\n");
  const parsed = {};
  for (const resp of bqnResponses) {
    const lines = resp.split("\n");
    const bqnIdLine = lines.find((line) => line.startsWith("BQN ID:"));
    if (bqnIdLine) {
      const [, bqnId] = bqnIdLine.split(":");
      if (bqnId) {
        parsed[bqnId.trim()] = resp;
      }
    }
  }
  return parsed;
}

const textsToHtml = source =>
  source.split('\n\n').map(textToHtml);

const textToHtml = source =>
  `<p style="line-height: 20px !important; margin-left: 12px">${
    source.trim().split('\n')
      .map(splitFieldAndValue)
      .filter(onlyAllowed)
      .map(arrangeUnits)
      .map(joinRecord)
      .map(keepIndentation)
      .join("<br>")
  }</p>`;

function parsePolicyDetails(policyText, idLine, reducedOutput = false) {
  let lines = policyText.trim().split("\n");
  lines = lines.filter(
    (line) =>
      !line.startsWith("Source:") &&
    (!reducedOutput ||
      line.search(/Burst|Blocking|Auto-Congestion-Management/) === -1)
  );
  if (idLine) {
    lines.splice(1, 0, idLine);
  }
  return lines.join('\n')
}

const areSameNodeIds = (expected, obtained) =>
  ( (one, another) => one.size === another.size && expected.every(id => obtained.includes(id)))(
    new Set(expected), new Set(obtained)
  );

const replaceBqnIdWithAll = input =>
  input.replace(/BQN ID: (\d+)/, 'BQN ID: All')

export const tryToMergeResults = (results) =>
  (Object.keys(results).length > 1
      ? requestOnlineBqnIds()
      : Promise.reject('must be more than 1 node'))
  .then( bqnIds =>
    areSameNodeIds(bqnIds, Object.keys(results)) !== true ? invalid('nodes not matching') :
    Object.values(results)
      .map(replaceBqnIdWithAll)
      .reduce( (result, current) =>
        result === null || result === current ? current :
        invalid('results not matching'), null)
  ).catch( () => Object.values(results).join("\n\n"))

const showPolicyModal = result =>
    showModalGeneric("hide", "Policy Details", textsToHtml(result), true);

export const showDynPolicyRateCluster = (policyName, bqnId) =>
  getPolicy(policyName)
    .then( response => {
      const policiesByBqn = parseClusterResponse(response);
      return Object.entries(policiesByBqn).reduce(
        (acc, [currentBqnId, policyText]) => {
          const policyIdLine = null;
          acc[currentBqnId] = parsePolicyDetails(policyText, policyIdLine);
          return acc;
        },
        {});
    })
    .then( infoBybqnId => 
      bqnId === 'all' ? tryToMergeResults(infoBybqnId) : infoBybqnId[bqnId]
    )
    .then(showPolicyModal)

export const showDynPolicyRate = (policyName, reducedOutput) =>
  getPolicy(policyName)
    .then(response => parsePolicyDetails(response, null, reducedOutput))
    .then(showPolicyModal)

